import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Switch,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { CellClickedEvent, ColDef } from "ag-grid-community";
import { useState } from "react";
import { NoPatientData } from "../../no-data";
import { TableGrid } from "../../table-grid";
import type { FhirSectionProps } from "../fhir-section";

type ViewMode = "individual" | "panel";

interface LabsViewData {
  _labsViewData: {
    individualView: {
      columnDefs: ColDef[];
      rowData: object[];
    };
    panelView: {
      columnDefs: ColDef[];
      rowData: object[];
    };
    panelDrillDownData: Map<string, object[]>;
  };
}

function LabsSection({
  sectionId,
  sectionName,
  rowData,
  handleCellClick,
  handleCellStyle,
  usingSectionSearch,
}: FhirSectionProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("individual");
  const [selectedPanel, setSelectedPanel] = useState<string | undefined>(undefined);

  const boxBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Extract the labs data from the passed props
  // The rowData will contain the embedded labs view data structure
  const labsData = rowData as LabsViewData[];
  const currentData = labsData?.[0]?._labsViewData;

  if (!currentData) {
    return (
      <Box
        id={sectionId}
        w={"100%"}
        mb={5}
        borderRadius={10}
        bg={boxBg}
        px={7}
        py={5}
        border="solid"
        borderColor={borderColor}
      >
        <HStack mb={5} w={"100%"} justifyContent={"space-between"}>
          <Heading size="md">{sectionName}</Heading>
        </HStack>
        <NoPatientData message="No Data Available" />
      </Box>
    );
  }

  // Determine which view to show
  let currentView;
  let showBackButton = false;

  if (viewMode === "individual") {
    currentView = currentData.individualView;
  } else if (selectedPanel) {
    // Show drill-down view for selected panel
    const panelData = currentData.panelDrillDownData.get(selectedPanel) || [];
    currentView = {
      columnDefs: currentData.individualView.columnDefs, // Use individual view columns for drill-down
      rowData: panelData,
    };
    showBackButton = true;
  } else {
    currentView = currentData.panelView;
  }

  const toggleViewMode = () => {
    setViewMode(current => (current === "individual" ? "panel" : "individual"));
    setSelectedPanel(undefined);
  };

  const clearDrillDown = () => {
    setSelectedPanel(undefined);
  };

  const toggleItem = (
    <Flex border="solid" borderRadius={"30px"} borderColor={borderColor} px={5} py={2}>
      <HStack fontWeight={"medium"}>
        <Text>Individual View</Text>
        <Switch
          isChecked={viewMode === "panel"}
          onChange={toggleViewMode}
          colorScheme={"blue"}
          size="md"
        />
        <Text>Panel View</Text>
      </HStack>
    </Flex>
  );

  const backButton = showBackButton && (
    <Button size="sm" onClick={clearDrillDown} variant="outline">
      ← Back to Panels
    </Button>
  );

  const handleCellClickWithDrillDown = (event: CellClickedEvent) => {
    // In panel view (not drill-down), clicking should drill down to panel details
    if (viewMode === "panel" && !selectedPanel && event.data?.id) {
      setSelectedPanel(event.data.id);
      return;
    }

    // Otherwise, use the normal cell click handler for side panel
    handleCellClick(sectionName, event);
  };

  return (
    <Box
      id={sectionId}
      w={"100%"}
      mb={5}
      borderRadius={10}
      bg={boxBg}
      px={7}
      py={5}
      border="solid"
      borderColor={borderColor}
    >
      <HStack mb={5} w={"100%"} justifyContent={"space-between"}>
        <HStack>
          <Heading size="md">{sectionName}</Heading>
          {backButton}
        </HStack>
        {toggleItem}
      </HStack>
      {currentView.rowData.length > 0 ? (
        <TableGrid
          defaultColDef={{
            flex: 1,
            onCellClicked: handleCellClickWithDrillDown,
            cellStyle: handleCellStyle,
          }}
          columnDefs={currentView.columnDefs}
          rowData={currentView.rowData}
          usingSectionSearch={usingSectionSearch}
        />
      ) : (
        <NoPatientData message="No Data Available" />
      )}
    </Box>
  );
}

export default LabsSection;
